import { cva } from 'class-variance-authority'

import type { AnchorHTMLAttributes, ButtonHTMLAttributes, JSX, ReactNode } from 'react'

/**
 * The marketing pages' button, speaking the `@openai/apps-sdk-ui` Button's vocabulary — `color`,
 * `variant`, `size` (ControlSize names) — without importing it: the package ships its own
 * stylesheet, and every rendered value here comes from the token layer and production instead.
 *
 * Only what the two pages render is offered. Production has exactly three buttons, and its own
 * light-mode CSS pairs them with SDK colours (`.btn-white` ~ `data-color="primary"`, `.btn-ghostd`
 * ~ `data-color="secondary"`):
 *
 *   nav "Get a demo"    primary   solid    lg
 *   hero "Get a demo"   primary   solid    2xl
 *   CTA band e-mail     secondary outline  2xl
 *
 * There is no `block`: width is the parent's job (`className="w-full"`), as is every margin and
 * position. Class strings must stay in this `.tsx` file — Tailwind's `@source` does not scan `.ts`.
 */
const button = cva(
  [
    // `border-0` and `py-0` clear a native <button>'s UA border and 1px padding; Preflight is absent.
    'inline-flex items-center justify-center whitespace-nowrap rounded-full border-0 py-0 no-underline cursor-pointer',
    'font-sans font-medium leading-none transition-colors',
    // Production resets the outline and draws no focus state at all; this ring is added, not measured.
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
  ],
  {
    variants: {
      // The colour and the variant only mean something as a pair; the pairs are the compounds below.
      color: { primary: '', secondary: '' },
      variant: { solid: '', outline: '' },
      size: {
        // The nav button. Measured on production: 36px from 900px up, 44px (the touch target) below,
        // and 12px text with 10px gutters at 380px and narrower. `max-[381px]` because Tailwind's
        // `max-*` is `width < N` and production's rule is `max-width: 380px`.
        lg: 'min-h-[44px] min-[900px]:min-h-[36px] px-[16px] text-base max-[381px]:px-[10px] max-[381px]:text-xs',
        '2xl': 'min-h-[44px] px-[18px] text-[16px]',
      },
    },
    compoundVariants: [
      {
        color: 'primary',
        variant: 'solid',
        class: 'bg-accent text-canvas hover:bg-accent-hover active:bg-accent-hover',
      },
      {
        color: 'secondary',
        variant: 'outline',
        class: 'bg-transparent text-ink shadow-ring hover:bg-fill-subtle active:bg-fill-subtle',
      },
    ],
    defaultVariants: { variant: 'solid' },
  },
)

type Tone = { color: 'primary'; variant?: 'solid' } | { color: 'secondary'; variant: 'outline' }

type Common = Tone & { size: 'lg' | '2xl'; className?: string; children: ReactNode }

type LinkProps = Common & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof Common> & { href: string }

type NativeProps = Common & Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof Common> & { href?: undefined }

export type ButtonProps = LinkProps | NativeProps

export default function Button(props: ButtonProps): JSX.Element {
  const { color, variant, size, className, ...rest } = props
  const classes = button({ color, variant, size, className })

  if (rest.href !== undefined) return <a {...(rest as AnchorHTMLAttributes<HTMLAnchorElement>)} className={classes} />

  return <button type="button" {...(rest as ButtonHTMLAttributes<HTMLButtonElement>)} className={classes} />
}
